/**
 * Graphics quality tier — a discrete Low / Medium / High setting, persisted to
 * localStorage so it survives reloads.
 *
 * The tier currently drives the renderer's pixel ratio, the single biggest
 * fill-rate lever: on a hi-DPI laptop devicePixelRatio is ~2, so we render ~4x
 * the fragments of a 1.0 buffer, and every per-fragment cost (terrain shader, fog
 * blend, grass, shadows, outlines) scales with it.
 *
 * effectivePixelRatio() = clamp(devicePixelRatio, ≤2) * tierScale.
 *  - High (default) keeps the full clamp (scale 1.0), so capable GPUs are
 *    bit-identical to before — no quality regression.
 *  - Medium / Low trade sharpness for fragment-fill headroom on weak GPUs.
 *
 * Kept a single enum (not a free slider) so the choices are legible and so more
 * dials (shadows, AA, foliage) can fold into the same tiers later without adding
 * UI. The settings panel renders a segmented control that writes setQualityTier()
 * and asks main.ts to re-apply the ratio; main.ts also reads effectivePixelRatio()
 * at boot and in the resize handler.
 */
const STORAGE_KEY = "tribez.graphicsQuality";

export type QualityTier = "low" | "medium" | "high";

export const QUALITY_TIERS: ReadonlyArray<{ tier: QualityTier; label: string }> = [
  { tier: "low", label: "Low" },
  { tier: "medium", label: "Medium" },
  { tier: "high", label: "High" },
];

export const DEFAULT_TIER: QualityTier = "high";

/** Hard cap on devicePixelRatio — bounds fill-rate on hi-DPI displays. Matches
 *  the original `Math.min(window.devicePixelRatio, 2)` clamp in main.ts. */
export const MAX_DEVICE_PIXEL_RATIO = 2;

const TIER_SCALE: Record<QualityTier, number> = {
  low: 0.6,
  medium: 0.8,
  high: 1.0,
};

let tier = load();

function isTier(v: unknown): v is QualityTier {
  return v === "low" || v === "medium" || v === "high";
}

function load(): QualityTier {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isTier(raw) ? raw : DEFAULT_TIER;
  } catch {
    return DEFAULT_TIER;
  }
}

export function getQualityTier(): QualityTier {
  return tier;
}

export function setQualityTier(t: QualityTier): void {
  tier = t;
  try {
    localStorage.setItem(STORAGE_KEY, t);
  } catch {
    /* private-mode / quota — keep the in-memory value, just don't persist. */
  }
}

/** The pixel ratio to hand `renderer.setPixelRatio()`: the hi-DPI-clamped device
 *  pixel ratio scaled by the current quality tier. */
export function effectivePixelRatio(): number {
  return Math.min(window.devicePixelRatio, MAX_DEVICE_PIXEL_RATIO) * TIER_SCALE[tier];
}
